"use client";

import { posthog } from "posthog-js";
import { track as vercelTrack } from "@vercel/analytics";
import { generateUUID } from "@drpcrd/utils";
import { createLinearErrorIssue } from "../../linear";

type Properties = Record<string, string | number | boolean | null>;

type ClientErrorProps = {
  error: unknown;
  userId: string | null;
  properties?: Properties;
  options?: {
    excludeVercel?: boolean;
    excludePostHog?: boolean;
    excludeLinear?: boolean;
  };
};

export type TrackClientErrorFunc<TrackProperties extends Properties> = ({
  userId,
  error,
  properties,
}: Pick<ClientErrorProps, "userId" | "error"> & {
  properties: TrackProperties;
}) => void;

export const trackClientError = ({
  error,
  userId,
  properties: props = {},
  options,
}: ClientErrorProps) => {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorStack = error instanceof Error ? (error.stack ?? null) : null;

  // PostHog tracking
  if (!options?.excludePostHog) {
    posthog.capture("client_side_error", {
      ...props,
      error: errorMessage,
      error_stack: errorStack,
      distinctId: userId ?? `untracked_user_${generateUUID()}`,
    });
  }

  // Vercel Analytics tracking
  if (!options?.excludeVercel) {
    vercelTrack("client_side_error", {
      user_id: userId,
      ...props,
      error: errorMessage,
    });
  }

  if (!options?.excludeLinear && process.env.VERCEL_ENV === "production") {
    createLinearErrorIssue(
      JSON.stringify({
        errorTitle:
          typeof props.error_title === "string" ? props.error_title : undefined,
        message: errorMessage,
        stack: errorStack,
        digest: typeof props.digest === "string" ? props.digest : null,
        replayUrl:
          typeof props.replay_url === "string" ? props.replay_url : null,
      }),
    );
  }

  // NOTE: We don't want to log to console in client-side for privacy reasons

  // ... add more analytics providers here as needed
};
